// client/src/components/pages/LexiStar/FleetTargets/TargetsEditor.tsx (ST-00248)
//
// The Targets section: the organisation's targets (every version, retired ones greyed) and, for
// holders of lexistar.reports.manage, adding one (a metric, what it applies to — the whole
// organisation, a route or a truck —, the value per day / week / month for an amount, from which
// day and optionally until when), changing it from a day (a new version: the earlier periods
// keep the old value; from its first day it is a correction), ending it, and retiring one set
// by mistake (with a reason; nothing is deleted). Two versions of one metric and scope never
// overlap: the API answers 409 with the one in the way, and the form says which.

import { useMemo, useState, type ReactNode } from 'react';
import { FaPlus } from 'react-icons/fa';
import UnifiedDatePicker from '../../../common/UnifiedDatePicker';
import { ThemedSelect } from '../../../../types/commonComponents';
import { cardClasses } from '../Dashboard/analyticsAtoms';
import { dayLabel, dayString, parseDay } from '../Dashboard/periodLogic';
import type {
  PerformanceTarget,
  TargetGrain,
  TargetInput,
  TargetMetric,
  TargetPatch,
  TargetResult,
  TargetRetire,
  TargetRevision,
  TargetScopes,
  TargetScopeType,
} from '../../../../types/lexistar-performance';
import {
  GRAIN_OPTIONS,
  STATUS_META,
  draftProblems,
  emptyDraft,
  inputOf,
  isCorrection,
  scopeOptions,
  scopeText,
  sortTargets,
  targetText,
  type TargetDraft,
} from './targetsLogic';

const inputClass =
  'w-full px-2.5 py-1.5 border border-slate-300 rounded-md bg-surface text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
const labelClass = 'block text-[11px] font-medium text-slate-600 mb-1';
const primaryButton =
  'inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg shadow-sm disabled:opacity-50';
const quietButton =
  'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 bg-surface border border-slate-300 hover:bg-slate-50 rounded-lg';

type ErrorLike = { response?: { status?: number; data?: { error?: string; message?: string; details?: string[] } }; message?: string };
export const apiErrorText = (err: unknown, fallback: string): string => {
  const e = err as ErrorLike;
  if (e?.response?.status === 403) return 'You do not have permission to change targets.';
  const d = e?.response?.data;
  return (d?.details && d.details.join('; ')) || d?.error || d?.message || e?.message || fallback;
};

function Field({ id, label, error, hint, children }: { id: string; label: string; error?: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      {children}
      {error ? (
        <p className="mt-1 text-xs text-rose-700" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

const dayOrNull = (s: string): Date | null => (s ? parseDay(s) : null);
const dayOf = (d: Date | null): string => (d ? dayString(d) : '');

/** The add form. */
function AddForm({
  metrics,
  scopes,
  today,
  onCreate,
  onCancel,
}: {
  metrics: readonly TargetMetric[];
  scopes: TargetScopes | null;
  today: string;
  onCreate: (input: TargetInput) => Promise<unknown>;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<TargetDraft>(() => emptyDraft(today));
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const m = metrics.find((x) => x.key === draft.metric);
  const problems = draftProblems(draft, metrics);
  const shown = touched ? problems : {};
  const set = (patch: Partial<TargetDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const scopeList = draft.scope_type === 'route' ? scopes?.routes || [] : scopes?.trucks || [];

  const submit = async () => {
    setTouched(true);
    if (Object.keys(problems).length) return;
    setBusy(true);
    setError(null);
    try {
      await onCreate(inputOf(draft, metrics));
    } catch (err) {
      setError(apiErrorText(err, 'The target could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className={`${cardClasses} p-4 space-y-3`}
      aria-labelledby="target-add-title"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <h3 id="target-add-title" className="text-sm font-semibold text-slate-900">
        Add a target
      </h3>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field id="target-metric" label="Target for" error={shown.metric} hint={m ? m.description : undefined}>
          <ThemedSelect
            id="target-metric"
            aria-label="Target for"
            value={draft.metric}
            onChange={(e) => {
              const key = String(e.target.value);
              const next = metrics.find((x) => x.key === key);
              set({
                metric: key as TargetDraft['metric'],
                scope_type: next && !next.scopes.includes(draft.scope_type) ? 'tenant' : draft.scope_type,
              });
            }}
          >
            <option value="">Choose…</option>
            {metrics.map((x) => (
              <option key={x.key} value={x.key}>
                {x.label}
              </option>
            ))}
          </ThemedSelect>
        </Field>
        <Field id="target-scope" label="Applies to" error={shown.scope_type}>
          <ThemedSelect
            id="target-scope"
            aria-label="Applies to"
            value={draft.scope_type}
            onChange={(e) => set({ scope_type: e.target.value as TargetScopeType, scope_id: '' })}
          >
            {scopeOptions(m).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </ThemedSelect>
        </Field>
        {draft.scope_type !== 'tenant' && (
          <Field
            id="target-scope-id"
            label={draft.scope_type === 'route' ? 'Route' : 'Truck'}
            error={shown.scope_id}
            hint={!scopeList.length ? (draft.scope_type === 'route' ? 'Your organisation has no routes yet.' : 'Your organisation has no trucks yet.') : undefined}
          >
            <ThemedSelect
              id="target-scope-id"
              aria-label={draft.scope_type === 'route' ? 'Route' : 'Truck'}
              value={draft.scope_id}
              onChange={(e) => set({ scope_id: String(e.target.value) })}
            >
              <option value="">Choose…</option>
              {scopeList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </ThemedSelect>
          </Field>
        )}
        <Field id="target-value" label={m ? `Target (${m.unit === 'count' ? 'trips' : m.unit === 'L/100km' ? 'L/100 km' : m.unit})` : 'Target'} error={shown.value} hint={m ? (m.direction === 'lower' ? 'Lower is better' : 'Higher is better') : undefined}>
          <input
            id="target-value"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={draft.value}
            onChange={(e) => set({ value: e.target.value })}
            className={inputClass}
          />
        </Field>
        {(!m || m.kind === 'amount') && (
          <Field id="target-grain" label="Per">
            <ThemedSelect id="target-grain" aria-label="Per" value={draft.grain} onChange={(e) => set({ grain: e.target.value as TargetGrain })}>
              {GRAIN_OPTIONS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </ThemedSelect>
          </Field>
        )}
        <Field id="target-from" label="From" error={shown.effective_from}>
          <UnifiedDatePicker
            id="target-from"
            value={dayOrNull(draft.effective_from)}
            onChange={(d) => set({ effective_from: dayOf(d) })}
            className={inputClass}
            wrapperClassName="w-full"
            title="From"
          />
        </Field>
        <Field id="target-to" label="Until (optional)" error={shown.effective_to} hint="Leave empty to keep it until you change it.">
          <UnifiedDatePicker
            id="target-to"
            value={dayOrNull(draft.effective_to)}
            onChange={(d) => set({ effective_to: dayOf(d) })}
            clearable
            placeholder="Until changed"
            minDate={dayOrNull(draft.effective_from)}
            className={inputClass}
            wrapperClassName="w-full"
            title="Until"
          />
        </Field>
        <Field id="target-notes" label="Notes (optional)" error={shown.notes}>
          <input id="target-notes" type="text" maxLength={500} value={draft.notes} onChange={(e) => set({ notes: e.target.value })} className={inputClass} />
        </Field>
      </div>
      {error && (
        <p className="text-sm text-rose-700" role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primaryButton} disabled={busy}>
          {busy ? 'Saving…' : 'Save target'}
        </button>
        <button type="button" className={quietButton} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

type Action = { kind: 'revise' | 'end' | 'retire'; target: PerformanceTarget };

/** Change from a day / end / retire one target. */
function ActionForm({
  action,
  metric,
  today,
  onRevise,
  onUpdate,
  onRetire,
  onDone,
}: {
  action: Action;
  metric?: TargetMetric;
  today: string;
  onRevise: (id: string, body: TargetRevision) => Promise<TargetResult>;
  onUpdate: (id: string, patch: TargetPatch) => Promise<unknown>;
  onRetire: (id: string, body: TargetRetire) => Promise<unknown>;
  onDone: () => void;
}) {
  const t = action.target;
  const [value, setValue] = useState(String(t.value));
  const [grain, setGrain] = useState<TargetGrain>(t.grain);
  const [from, setFrom] = useState(today > t.effective_from ? today : t.effective_from);
  const [until, setUntil] = useState(t.effective_to || '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const title = action.kind === 'revise' ? 'Change the target' : action.kind === 'end' ? 'End the target' : 'Retire the target';

  const run = async () => {
    setError(null);
    if (action.kind === 'revise') {
      const n = Number(value);
      if (value.trim() === '' || !Number.isFinite(n) || n < 0) return setError('Enter the new target as a number');
      if (metric?.unit === '%' && n > 100) return setError('A percentage is at most 100');
    }
    if (action.kind === 'retire' && reason.trim().length < 3) return setError('Say why it is retired');
    setBusy(true);
    try {
      if (action.kind === 'revise') {
        await onRevise(t.id, { value: Number(value), ...(metric?.kind === 'amount' ? { grain } : {}), from, version: t.version });
      } else if (action.kind === 'end') {
        await onUpdate(t.id, { effective_to: until || null, version: t.version });
      } else {
        await onRetire(t.id, { reason: reason.trim(), version: t.version });
      }
      onDone();
    } catch (err) {
      setError(apiErrorText(err, 'The change could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className={`${cardClasses} p-4 space-y-3 border-primary-200`}
      aria-labelledby="target-action-title"
      onSubmit={(e) => {
        e.preventDefault();
        void run();
      }}
    >
      <div>
        <h3 id="target-action-title" className="text-sm font-semibold text-slate-900">
          {title}
        </h3>
        <p className="text-xs text-slate-600">
          {metric?.label ?? t.metric} · {scopeText(t)} · {targetText(metric, t)} from {dayLabel(t.effective_from)}
          {t.effective_to ? ` to ${dayLabel(t.effective_to)}` : ''}
        </p>
      </div>
      {action.kind === 'revise' && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Field id="target-new-value" label="New target">
            <input id="target-new-value" type="number" inputMode="decimal" min={0} step="any" value={value} onChange={(e) => setValue(e.target.value)} className={inputClass} />
          </Field>
          {metric?.kind === 'amount' && (
            <Field id="target-new-grain" label="Per">
              <ThemedSelect id="target-new-grain" aria-label="Per" value={grain} onChange={(e) => setGrain(e.target.value as TargetGrain)}>
                {GRAIN_OPTIONS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </ThemedSelect>
            </Field>
          )}
          <Field
            id="target-new-from"
            label="From"
            hint={
              isCorrection(t, from)
                ? 'From its first day: this corrects the target for every period it covers.'
                : `Periods before ${dayLabel(from)} keep ${targetText(metric, t)}.`
            }
          >
            <UnifiedDatePicker
              id="target-new-from"
              value={dayOrNull(from)}
              onChange={(d) => d && setFrom(dayOf(d))}
              minDate={dayOrNull(t.effective_from)}
              maxDate={dayOrNull(t.effective_to || '')}
              className={inputClass}
              wrapperClassName="w-full"
              title="From"
            />
          </Field>
        </div>
      )}
      {action.kind === 'end' && (
        <Field id="target-until" label="Last day" hint="Leave empty to keep it until it is changed.">
          <UnifiedDatePicker
            id="target-until"
            value={dayOrNull(until)}
            onChange={(d) => setUntil(dayOf(d))}
            clearable
            placeholder="Until changed"
            minDate={dayOrNull(t.effective_from)}
            className={inputClass}
            wrapperClassName="w-full"
            title="Last day"
          />
        </Field>
      )}
      {action.kind === 'retire' && (
        <Field id="target-retire-reason" label="Why is it retired?" hint="A retired target is kept for the record and never counts again.">
          <input id="target-retire-reason" type="text" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} />
        </Field>
      )}
      {error && (
        <p className="text-sm text-rose-700" role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={action.kind === 'retire' ? primaryButton.replace('bg-primary-500 hover:bg-primary-600', 'bg-rose-600 hover:bg-rose-700') : primaryButton} disabled={busy}>
          {busy ? 'Saving…' : action.kind === 'revise' ? 'Save the change' : action.kind === 'end' ? 'Save the last day' : 'Retire'}
        </button>
        <button type="button" className={quietButton} onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function TargetsEditor({
  targets,
  metrics,
  scopes,
  today,
  canManage,
  onCreate,
  onRevise,
  onUpdate,
  onRetire,
}: {
  targets: PerformanceTarget[];
  metrics: readonly TargetMetric[];
  scopes: TargetScopes | null;
  today: string;
  canManage: boolean;
  onCreate: (input: TargetInput) => Promise<unknown>;
  onRevise: (id: string, body: TargetRevision) => Promise<TargetResult>;
  onUpdate: (id: string, patch: TargetPatch) => Promise<unknown>;
  onRetire: (id: string, body: TargetRetire) => Promise<unknown>;
}) {
  const [adding, setAdding] = useState(false);
  const [action, setAction] = useState<Action | null>(null);
  const [showRetired, setShowRetired] = useState(false);
  const byKey = useMemo(() => new Map(metrics.map((m) => [m.key, m])), [metrics]);
  const list = sortTargets(
    targets.filter((t) => showRetired || t.status !== 'retired'),
    metrics
  );
  const retiredCount = targets.filter((t) => t.status === 'retired').length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-700">
          Targets are what the plan says each period should bring. Change one from a day and the periods before keep the old value.
        </p>
        {canManage && !adding && (
          <button
            type="button"
            className={primaryButton}
            onClick={() => {
              setAction(null);
              setAdding(true);
            }}
          >
            <FaPlus aria-hidden="true" /> Add a target
          </button>
        )}
      </div>
      {!canManage && (
        <p className="text-xs text-slate-500">Setting targets needs the “Manage LexiStar Report Schedules and Targets” permission.</p>
      )}
      {adding && (
        <AddForm
          metrics={metrics}
          scopes={scopes}
          today={today}
          onCreate={async (input) => {
            await onCreate(input);
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
        />
      )}
      {action && (
        <ActionForm
          key={`${action.kind}|${action.target.id}`}
          action={action}
          metric={byKey.get(action.target.metric)}
          today={today}
          onRevise={onRevise}
          onUpdate={onUpdate}
          onRetire={onRetire}
          onDone={() => setAction(null)}
        />
      )}
      <div className={`${cardClasses} overflow-hidden`}>
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4">
          <h2 id="targets-list-title" className="text-sm font-semibold text-slate-900">
            Your targets
          </h2>
          {retiredCount > 0 && (
            <label className="inline-flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
              Show retired ({retiredCount})
            </label>
          )}
        </div>
        {list.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-600">No targets yet.{canManage ? ' Add one to compare the actual figures with it.' : ''}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" aria-labelledby="targets-list-title">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th scope="col" className="px-4 py-2 font-medium">Target for</th>
                  <th scope="col" className="px-3 py-2 font-medium">Applies to</th>
                  <th scope="col" className="px-3 py-2 font-medium">Target</th>
                  <th scope="col" className="px-3 py-2 font-medium">From</th>
                  <th scope="col" className="px-3 py-2 font-medium">Until</th>
                  <th scope="col" className="px-3 py-2 font-medium">Status</th>
                  {canManage && <th scope="col" className="px-4 py-2 font-medium text-right"><span className="sr-only">Actions</span></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((t) => {
                  const m = byKey.get(t.metric);
                  const live = t.status !== 'retired';
                  return (
                    <tr key={t.id} className={live ? 'hover:bg-slate-50' : 'text-slate-500'}>
                      <th scope="row" className="px-4 py-2 text-left font-medium text-slate-900">
                        {m?.label ?? t.metric}
                        {t.notes && <span className="block text-xs font-normal text-slate-500">{t.notes}</span>}
                        {t.retire_reason && <span className="block text-xs font-normal text-slate-500">Retired: {t.retire_reason}</span>}
                      </th>
                      <td className="px-3 py-2 text-slate-700">{scopeText(t)}</td>
                      <td className="px-3 py-2 tabular-nums text-slate-800 whitespace-nowrap">{targetText(m, t)}</td>
                      <td className="px-3 py-2 tabular-nums text-slate-700 whitespace-nowrap">{dayLabel(t.effective_from)}</td>
                      <td className="px-3 py-2 tabular-nums text-slate-700 whitespace-nowrap">{t.effective_to ? dayLabel(t.effective_to) : 'Until changed'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_META[t.status].pill}`}>{STATUS_META[t.status].label}</span>
                      </td>
                      {canManage && (
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          {live && (
                            <div className="inline-flex gap-1.5">
                              <button type="button" className={quietButton} onClick={() => { setAdding(false); setAction({ kind: 'revise', target: t }); }} aria-label={`Change ${m?.label ?? t.metric} target from ${dayLabel(t.effective_from)}`}>
                                Change
                              </button>
                              <button type="button" className={quietButton} onClick={() => { setAdding(false); setAction({ kind: 'end', target: t }); }} aria-label={`End ${m?.label ?? t.metric} target from ${dayLabel(t.effective_from)}`}>
                                End
                              </button>
                              <button type="button" className={quietButton} onClick={() => { setAdding(false); setAction({ kind: 'retire', target: t }); }} aria-label={`Retire ${m?.label ?? t.metric} target from ${dayLabel(t.effective_from)}`}>
                                Retire
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
